import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  Divider,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Palette as PaletteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { ColorFormProps, ColorFormData, ColorValidationErrors, COLOR_CONSTANTS } from '../../types/color';
import { colorUtils } from '../../utils/colorUtils';
import { colorValidation } from '../../utils/colorValidation';
import { SynonymManager } from '../SynonymManager';

const ColorForm: React.FC<ColorFormProps> = ({
  color,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [formData, setFormData] = useState<ColorFormData>({
    name: '',
    hexadecimal: '#000000',
    cmyk: '',
    rgb: '',
  });
  const [errors, setErrors] = useState<ColorValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Initialize form data when color prop changes
  useEffect(() => {
    if (color) {
      setFormData({
        name: color.name,
        hexadecimal: color.hexadecimal,
        cmyk: color.cmyk || '',
        rgb: color.rgb || '',
      });
    } else {
      setFormData({
        name: '',
        hexadecimal: '#000000',
        cmyk: '',
        rgb: '',
      });
    }
  }, [color]);

  // Auto-generate RGB and CMYK when hex changes
  useEffect(() => {
    if (formData.hexadecimal && colorUtils.isValidHex(formData.hexadecimal)) {
      const rgb = colorUtils.hexToRgb(formData.hexadecimal);
      if (rgb) {
        const cmyk = colorUtils.rgbToCmyk(rgb.r, rgb.g, rgb.b);
        setFormData(prev => ({
          ...prev,
          rgb: colorUtils.formatRgb(rgb.r, rgb.g, rgb.b),
          cmyk: colorUtils.formatCmyk(cmyk.c, cmyk.m, cmyk.y, cmyk.k),
        }));
      }
    }
  }, [formData.hexadecimal]);

  const validateField = (field: keyof ColorFormData, value: any): string | undefined => {
    return colorValidation.validateField(field, value);
  };

  const validateForm = (): boolean => {
    const validationResult = colorValidation.validateColorForm(formData);
    setErrors(validationResult);
    return colorValidation.isColorFormValid(formData);
  };

  const handleFieldChange = (field: keyof ColorFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (field in errors && (errors as any)[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleFieldBlur = (field: keyof ColorFormData) => {
    setTouched(prev => ({
      ...prev,
      [field]: true,
    }));

    const error = validateField(field, formData[field]);
    setErrors(prev => ({
      ...prev,
      [field]: error,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Mark all fields as touched
    setTouched({
      name: true,
      hexadecimal: true,
      cmyk: true,
      rgb: true,
    });

    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleColorPickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFieldChange('hexadecimal', e.target.value);
  };

  const generateRandomColor = () => {
    const randomColor = COLOR_CONSTANTS.DEFAULT_COLORS[
      Math.floor(Math.random() * COLOR_CONSTANTS.DEFAULT_COLORS.length)
    ];
    handleFieldChange('hexadecimal', randomColor);
  };

  const getFieldError = (field: keyof ColorFormData): string | undefined => {
    return touched[field] ? errors[field] : undefined;
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Typography variant="h6" gutterBottom>
          {color ? 'Edit Color' : 'Create New Color'}
        </Typography>
        
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {/* Color Preview */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Color Preview
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    height: 120,
                    backgroundColor: formData.hexadecimal,
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: colorUtils.getContrastColor(formData.hexadecimal),
                    fontWeight: 'bold',
                    mb: 2,
                  }}
                >
                  {formData.name || 'Color Preview'}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <input
                    type="color"
                    value={formData.hexadecimal}
                    onChange={handleColorPickerChange}
                    style={{
                      width: '100%',
                      height: 40,
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                    }}
                  />
                  <IconButton onClick={generateRandomColor} size="small">
                    <RefreshIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Form Fields */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Color Name"
                  value={formData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  onBlur={() => handleFieldBlur('name')}
                  error={!!getFieldError('name')}
                  helperText={getFieldError('name')}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PaletteIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Hexadecimal"
                  value={formData.hexadecimal}
                  onChange={(e) => handleFieldChange('hexadecimal', e.target.value)}
                  onBlur={() => handleFieldBlur('hexadecimal')}
                  error={!!getFieldError('hexadecimal')}
                  helperText={getFieldError('hexadecimal') || 'Format: #RRGGBB (e.g., #FF0000)'}
                  required
                  placeholder="#000000"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="RGB"
                  value={formData.rgb}
                  onChange={(e) => handleFieldChange('rgb', e.target.value)}
                  onBlur={() => handleFieldBlur('rgb')}
                  error={!!getFieldError('rgb')}
                  helperText={getFieldError('rgb') || 'Format: R, G, B (e.g., 255, 0, 0)'}
                  placeholder="255, 0, 0"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="CMYK"
                  value={formData.cmyk}
                  onChange={(e) => handleFieldChange('cmyk', e.target.value)}
                  onBlur={() => handleFieldBlur('cmyk')}
                  error={!!getFieldError('cmyk')}
                  helperText={getFieldError('cmyk') || 'Format: C, M, Y, K (e.g., 0, 100, 100, 0)'}
                  placeholder="0, 100, 100, 0"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Color Suggestions */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Quick Colors
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {COLOR_CONSTANTS.DEFAULT_COLORS.map((colorHex) => (
              <Chip
                key={colorHex}
                label=""
                onClick={() => handleFieldChange('hexadecimal', colorHex)}
                sx={{
                  backgroundColor: colorHex,
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  cursor: 'pointer',
                  border: formData.hexadecimal === colorHex ? '3px solid #1976d2' : '1px solid #ccc',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={loading}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={<SaveIcon />}
          >
            {loading ? 'Saving...' : (color ? 'Update Color' : 'Create Color')}
          </Button>
        </Box>

        {/* Synonym Management - Only show when editing existing color */}
        {color && (
          <SynonymManager
            entityType="color"
            entityId={color.id}
            entityName={color.name}
            disabled={loading}
          />
        )}
      </Box>
    </Paper>
  );
};

export default ColorForm;
